
import { useState, useMemo, useCallback, useEffect, useRef, lazy, Suspense } from 'react'
import type { LucideProps } from 'lucide-react'
import dynamicIconImports from 'lucide-react/dynamicIconImports'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from '@/components/ui/command'
import { getGroupedCommands, type CommandContext, type TabId } from '@/lib/canvas/command-registry'

/* ------------------------------------------------------------------ */
/*  Lazy icon loader                                                   */
/* ------------------------------------------------------------------ */

const iconCache: Record<string, React.LazyExoticComponent<React.ComponentType<LucideProps>>> = {}

function LazyIcon({ name, className }: { name: string; className?: string }) {
  if (!(name in dynamicIconImports)) return null

  if (!iconCache[name]) {
    iconCache[name] = lazy(dynamicIconImports[name as keyof typeof dynamicIconImports])
  }

  const IconComponent = iconCache[name]
  return (
    <Suspense fallback={<div className="size-4" />}>
      <IconComponent className={className} />
    </Suspense>
  )
}

/* ------------------------------------------------------------------ */
/*  Tabs                                                               */
/* ------------------------------------------------------------------ */

const TABS: { id: TabId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'nodes', label: 'Nodes' },
  { id: 'tools', label: 'Tools' },
  { id: 'canvas', label: 'Canvas' },
]

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  context: CommandContext
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function CommandPalette({ open, onOpenChange, context }: CommandPaletteProps) {
  const [activeTab, setActiveTab] = useState<TabId>('all')

  // Force-enable scroll on the command list by handling wheel events directly.
  // Radix Dialog's RemoveScroll blocks wheel events globally; we intercept
  // them at the list level before RemoveScroll can preventDefault().
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    let el: HTMLElement | null = null
    let handler: ((e: WheelEvent) => void) | null = null

    // Wait for DOM to render
    const timer = setTimeout(() => {
      el = listRef.current?.querySelector<HTMLElement>('[cmdk-list]') ?? listRef.current
      if (!el) return

      handler = (e: WheelEvent) => {
        const { scrollTop, scrollHeight, clientHeight } = el!
        const atTop = scrollTop === 0 && e.deltaY < 0
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0
        if (!atTop && !atBottom) {
          e.stopPropagation()
          el!.scrollTop += e.deltaY
        }
      }
      el.addEventListener('wheel', handler, { passive: false, capture: true })
    }, 50)

    return () => {
      clearTimeout(timer)
      if (el && handler) el.removeEventListener('wheel', handler, { capture: true })
    }
  }, [open])

  const grouped = useMemo(
    () => getGroupedCommands(context, activeTab),
    [context, activeTab],
  )

  const handleSelect = useCallback(
    (commandId: string) => {
      // Search across all commands (not just filtered)
      const all = getGroupedCommands(context, 'all')
      const allCommands = Array.from(all.values()).flat()
      const cmd = allCommands.find((c) => c.id === commandId)
      if (cmd) {
        onOpenChange(false)
        requestAnimationFrame(() => cmd.execute(context))
      }
    },
    [context, onOpenChange],
  )

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value) setActiveTab('all')
      onOpenChange(value)
    },
    [onOpenChange],
  )

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Command Palette"
      description="Search for a command to run..."
      showCloseButton={false}
    >
      <CommandInput placeholder="Type a command..." />

      {/* Tab bar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div ref={listRef}>
      <CommandList className="max-h-[400px]">
        <CommandEmpty>No commands found.</CommandEmpty>
        {Array.from(grouped.entries()).map(([group, commands]) => (
          <CommandGroup key={group} heading={group}>
            {commands.map((cmd) => (
              <CommandItem
                key={cmd.id}
                value={`${cmd.label} ${cmd.keywords?.join(' ') ?? ''}`}
                onSelect={() => handleSelect(cmd.id)}
              >
                {cmd.icon && <LazyIcon name={cmd.icon} className="size-4 text-muted-foreground" />}
                <span>{cmd.label}</span>
                {cmd.shortcut && (
                  <CommandShortcut>{cmd.shortcut}</CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      </div>
    </CommandDialog>
  )
}
